import React, { useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Play } from "lucide-react";

interface VideoTestimonialsProps {
  embedded?: boolean;
}

const extractYouTubeId = (url: string): string | null => {
  const match = url.match(/\/embed\/([^?]+)/);
  return match ? match[1] : null;
};

const YouTubeFacade: React.FC<{ url: string; title: string }> = ({ url, title }) => {
  const [loaded, setLoaded] = useState(false);
  const videoId = extractYouTubeId(url);
  const handleLoad = useCallback(() => setLoaded(true), []);

  if (loaded) {
    return (
      <iframe
        src={`${url}?autoplay=1`}
        title={title}
        className="w-full h-full rounded-md"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    );
  }

  return (
    <div
      className="w-full h-full rounded-md relative cursor-pointer bg-muted flex items-center justify-center"
      onClick={handleLoad}
      role="button"
      aria-label={`Reproduzir: ${title}`}
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && handleLoad()}
    >
      {videoId && (
        <img
          src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
          alt={title}
          width={480}
          height={360}
          className="w-full h-full object-cover rounded-md"
          loading="lazy"
        />
      )}
      <div className="absolute inset-0 bg-black/30 flex items-center justify-center rounded-md">
        <div className="bg-red-600 rounded-full p-3 shadow-lg hover:bg-red-700 transition-colors">
          <Play className="h-6 w-6 text-white fill-current" />
        </div>
      </div>
    </div>
  );
};

const VideoTestimonials: React.FC<VideoTestimonialsProps> = ({ embedded = false }) => {
  const videos = [
    {
      id: "1",
      quote:
        "Fiquei impressionado com a competência deles e clareza do produto. São pessoas de extrema confiança",
      name: "Douglas Costa",
      role: "Oftamologista",
      url: "https://www.youtube.com/embed/5I63NH_70gM",
    },
    {
      id: "2",
      quote:
        "Já estava buscando uma forma de reduzir custos e tributos. Redução para 1/3 do valor que pagávamos.",
      name: "Francisco Antonio Von Scheming",
      role: "Ultrassonografista",
      url: "https://www.youtube.com/embed/5moDTEKM5PU",
    },
    {
      id: "4",
      quote:
        "Foi um serviço que impactou de maneira considerável, cerca de 70% de redução nos impostos. Uma economia muito grande",
      name: "Manoela Amorim",
      role: "Ginecologista e Obstetrae",
      url: "https://www.youtube.com/embed/8oSXRI_91lU",
    },
  ];

  const Grid = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {videos.map((video) => (
        <Card key={video.id} className="overflow-hidden">
          <CardContent>
            <AspectRatio ratio={16 / 9}>
              {video.url.endsWith(".mp4") ? (
                <video
                  src={video.url}
                  className="w-full h-full rounded-md"
                  controls
                  preload="none"
                  aria-label={`${video.name} - Depoimento em vídeo`}
                />
              ) : (
                <YouTubeFacade url={video.url} title={`${video.name} - Depoimento em vídeo`} />
              )}
            </AspectRatio>
            <div className="mt-4">
              <blockquote className="text-muted-foreground italic">{video.quote}</blockquote>
              <div className="mt-3">
                <p className="font-semibold text-foreground">{video.name}</p>
                <p className="text-sm text-muted-foreground">{video.role}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  if (embedded) {
    return <Grid />;
  }

  return (
    <section className="py-16 px-6 bg-background">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            Depoimentos em vídeo
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto text-balance">
            Ouça a experiência de quem já implementou a solução da TaxHealth
          </p>
        </div>

        <Grid />
      </div>
    </section>
  );
};

export default VideoTestimonials;
